// src/components/POIGalleryEditor.tsx
import { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { X, Plus, Upload } from 'lucide-react';
import { api, POIImage, edgeFetchWithFallback, buildEdgeImageSrc } from '../services/api';
import { isValidImageUrl } from '../lib/utils';
import { EdgeCaptureImage } from './EdgeCaptureImage';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from 'react-i18next';

interface Props {
  poiId: string;
  currentUserRole: string;
  edgeIp?: string;
  edgePort?: number;
  // v0.4.9.1.13: hostname preferred over IP for HTTPS (cert SAN matches).
  edgeHostname?: string;
}

interface POICandidate {
  id: number;
  timestamp: string;
  camera_name: string;
  file_path: string;
  role: string;
  location: string | null;
}

// v0.4.9.1.9: increased from 10 to 20 (matches Authorized Persons limit).
// Per user: 10 is too little for a full setup to reduce false positives.
// Gallery mitigates angle/touch/cup issues but doesn't eliminate them —
// more gallery images = better matching accuracy.
const MAX_GALLERY_IMAGES = 20;

// v0.4.9.1.14: replaced v0.4.9.1.13's edgeFetch with the central helper.
// Old behavior: hostname → IP, never falling back. If the hostname DNS didn't
// resolve (PWA user on local WiFi without router DNS for *.isentinel360),
// the request just failed silently.
// New behavior: try hostname (HTTPS for APK) → fall back to IP+HTTP (PWA).
// See api.ts → edgeFetchWithFallback for the full contract.
async function edgeFetch(edgeHostname: string | undefined, edgeIp: string | undefined, edgePort: number | undefined, path: string, init: RequestInit = {}) {
  const res = await edgeFetchWithFallback(
    { hostname: edgeHostname, edgeIp, edgePort },
    path,
    init
  );
  // Preserve the old throw-on-error semantics for callers that depend on
  // catching a failed fetch. Returning null when both endpoints fail.
  if (res === null) {
    throw new Error('Edge not reachable');
  }
  return res;
}

// Helper to resolve image URLs
function resolveImageUrl(url: string): string {
  if (!url) return '';
  if (url.startsWith('http://') || url.startsWith('https://')) return url;
  if (url.startsWith('/')) return `https://api.isentinel360.com${url}`;
  return `https://api.isentinel360.com/${url}`;
}

// COMPONENT
export function POIGalleryEditor({ poiId, currentUserRole, edgeIp, edgePort, edgeHostname }: Props) {
  const [gallery, setGallery] = useState<POIImage[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { user } = useAuth();
  const { t } = useTranslation();

  const [pickerOpen, setPickerOpen] = useState(false);
  const [candidates, setCandidates] = useState<POICandidate[]>([]);
  const [candLoading, setCandLoading] = useState(false);
  const [candError, setCandError] = useState<string | null>(null);

  // Filter state – matches POIFormModal exactly
  const now = new Date();
  const [date, setDate] = useState(now.toISOString().split('T')[0]);
  const [hour, setHour] = useState<number | ''>(now.getHours());
  const [minBlock, setMinBlock] = useState<number | ''>(Math.floor(now.getMinutes() / 10));
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  // Upload from disk state
  const [uploading, setUploading] = useState(false);

  // v0.4.9.1.9: includes similarity so we can show "Already in gallery" for
  // exact matches vs "Similar — add anyway?" for partial matches.
  const [duplicateModal, setDuplicateModal] = useState<{
    existing: { id: string; code: string; photoUrl: string; similarity?: number };
    embedding: number[];
    source: 'candidate' | 'upload';
    payload: any;
    /** true if this image is already in the gallery (sim ~1.0) — no Add Anyway button */
    isExactMatch?: boolean;
  } | null>(null);

  const canRemove = !['guard'].includes(currentUserRole);

  const fetchGallery = useCallback(async () => {
    if (!poiId) return;
    setLoading(true);
    setError(null);
    try {
      const { images } = await api.getPoiImages(poiId);
      setGallery(images);
    } catch (err: any) {
      setError(err.message || 'Failed to load gallery');
      setGallery([]);
    } finally {
      setLoading(false);
    }
  }, [poiId]);

  // ✅ fetchCandidates – exactly like POIFormModal, but filters by role='poi'
  const fetchCandidates = useCallback(async () => {
    if (!edgeHostname && (!edgeIp || !edgePort)) {
      setCandError('Edge not configured');
      return;
    }
    setCandLoading(true);
    setCandError(null);
    try {
      const params = new URLSearchParams();
      if (date) {
        const start = new Date(date + 'T00:00:00Z');
        const end = new Date(date + 'T23:59:59Z');
        params.set('startDate', start.toISOString());
        params.set('endDate', end.toISOString());
      } else {
        const sevenDaysAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000);
        params.set('startDate', sevenDaysAgo.toISOString());
      }
      const capsRes = await edgeFetch(edgeHostname, edgeIp, edgePort, `/captures?${params.toString()}`);
      const caps = await capsRes.json();

      // v0.4.9.1.8 bug fix: filter by POI role family, not exact string.
      // The captures table uses canonical roles: poi_person, poi_mix, poi_vehicle
      // (legacy 'poi' is migrated to 'poi_person' on read by agent.js).
      // The previous filter `item.role !== 'poi'` excluded everything because
      // the actual stored roles are poi_person/poi_mix/poi_vehicle.
      // The "Photo URL → Add from Recent" in POIFormModal works because it uses
      // recentFaces from useDemoStore (different source, different filter).
      // This filter now matches POI_FACE_ROLES ∪ POI_PLATE_ROLES — i.e. any capture
      // that came from a POI camera (person or vehicle).
      const POI_ROLES = new Set(['poi_person', 'poi_mix', 'poi_vehicle', 'poi']);

      const filtered = caps.filter((item: any) => {
        if (!POI_ROLES.has(item.role)) return false;
        const d = new Date(item.timestamp);
        if (hour !== '' && d.getHours() !== Number(hour)) return false;
        if (minBlock !== '' && Math.floor(d.getMinutes() / 10) !== Number(minBlock)) return false;
        return true;
      });

      setCandidates(filtered);
    } catch (err: any) {
      setCandError(err.message || 'Failed to load');
      setCandidates([]);
    } finally {
      setCandLoading(false);
    }
  }, [edgeIp, edgePort, edgeHostname, date, hour, minBlock]);

  useEffect(() => {
    if (pickerOpen) {
      fetchCandidates();
    }
  }, [pickerOpen, date, hour, minBlock, fetchCandidates, refreshTrigger]);

  // 🔄 Fetch gallery on mount and when poiId changes
  useEffect(() => {
    if (poiId) {
      fetchGallery();
    }
  }, [poiId, fetchGallery]);

  // Helper: get human-readable role name
  const getRoleName = (role: string): string => {
    switch (role) {
      case 'superadmin': return 'Super Admin';
      case 'cadmin': return 'Chief Admin';
      case 'admin': return 'Admin';
      case 'guard': return 'Guard';
      default: return role;
    }
  };

  // Generate justification automatically
  const generateJustification = (): string => {
    const roleName = getRoleName(currentUserRole);
    const userName = user?.name || 'Unknown';
    return `Added from ${roleName} Dashboard by ${userName}`;
  };

  // ===== DUPLICATE CHECK HELPER =====
  // v0.4.9.1.9: also returns similarity so the UI can decide between
  // "already there, can't add" (similarity ~1.0) and "similar, you decide" (lower).
  const checkForDuplicate = async (embedding: number[]): Promise<{
    duplicate: boolean;
    existing?: { id: string; code: string; photoUrl: string; similarity?: number };
  }> => {
    try {
      const result = await api.post('/api/duplicates/check-embedding', { embedding });
      if (result.duplicate && result.existing) {
        return {
          duplicate: true,
          existing: {
            id: result.existing.id,
            code: result.existing.code || 'Unknown',
            photoUrl: result.existing.photo_url || '',
            similarity: typeof result.existing.similarity === 'number' ? result.existing.similarity : undefined,
          }
        };
      }
      return { duplicate: false };
    } catch (err) {
      console.warn('[POIGalleryEditor] Duplicate check failed:', err);
      return { duplicate: false };
    }
  };

  // Helper: cosine similarity between two vectors
  function cosineSimilarity(a: number[], b: number[]): number {
    let dot = 0, normA = 0, normB = 0;
    for (let i = 0; i < a.length; i++) {
      dot += a[i] * b[i];
      normA += a[i] * a[i];
      normB += b[i] * b[i];
    }
    if (normA === 0 || normB === 0) return 0;
    return dot / (Math.sqrt(normA) * Math.sqrt(normB));
  }

  // Check if embedding already exists in the current gallery
  const isDuplicateInGallery = (embedding: number[], gallery: POIImage[]): boolean => {
    const THRESHOLD = 0.65;
    for (const img of gallery) {
      if (!img.faceEmbedding) continue;
      if (img.faceEmbedding.length !== embedding.length) continue;
      const sim = cosineSimilarity(embedding, img.faceEmbedding);
      if (sim >= THRESHOLD) return true;
    }
    return false;
  };

  const performAddCandidate = async (capture: POICandidate, embedding: number[], opts?: { bypassGalleryCheck?: boolean }) => {
    try {
      // Internal duplicate check (can be bypassed if user already acknowledged
      // the global duplicate and clicked "Add Anyway").
      if (!opts?.bypassGalleryCheck && isDuplicateInGallery(embedding, gallery)) {
        alert(t('common.gallery.duplicateFace'));
        return;
      }

      const justification = generateJustification();
      const token = localStorage.getItem('authToken');
      if (!token) { alert('Not authenticated.'); return; }
      let userId = '';
      try { userId = JSON.parse(atob(token.split('.')[1])).userId || ''; } catch {}

      const res = await edgeFetch(edgeHostname, edgeIp, edgePort, '/promote-capture', {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({
          capture_id: capture.id,
          poi_action: 'existing',
          poi_id: poiId,
          justification: justification.trim(),
          created_by: userId,
        }),
      });
      if (!res.ok) {
        const body = await res.text().catch(() => '');
        alert(`Add failed (${res.status}): ${body}`);
        return;
      }
      setCandidates(prev => prev.filter(c => c.id !== capture.id));
      await fetchGallery();
      alert(t('common.gallery.imageAdded'));
    } catch (err: any) {
      alert(`Add error: ${err.message || err}`);
    }
  };

  const performUploadFromDisk = async (dataUrl: string, embedding: number[], opts?: { bypassGalleryCheck?: boolean }) => {
    try {
      if (!opts?.bypassGalleryCheck && isDuplicateInGallery(embedding, gallery)) {
        alert(t('common.gallery.duplicateFace'));
        return;
      }

      const uploadRes = await api.uploadPhoto(dataUrl);
      const photoUrl = uploadRes.url;

      const token = localStorage.getItem('authToken');
      let userId = '';
      try { userId = JSON.parse(atob(token?.split('.')[1] || '')).userId || ''; } catch {}

      const justification = generateJustification();

      await api.addPoiImage(poiId, {
        photo_url: photoUrl,
        face_embedding: embedding,
        created_by: userId,
        justification: justification.trim(),
      });

      await fetchGallery();
      alert(t('common.gallery.imageAdded'));
    } catch (err: any) {
      console.error('Upload error:', err);
      alert('Failed to upload image: ' + (err.message || 'unknown error'));
    }
  };

  const handleRemove = async (image: POIImage) => {
    if (!confirm(t('common.gallery.removeConfirm'))) return;
    try {
      await api.deletePoiImage(poiId, image.id);
      await fetchGallery();
    } catch (err: any) {
      alert(`Failed to remove: ${err.message || err}`);
    }
  };

  const handleAddCandidate = async (capture: POICandidate) => {
    try {
      // Get capture details to get the embedding
      const detailsRes = await edgeFetch(edgeHostname, edgeIp, edgePort, `/captures/${capture.id}/details`);
      if (!detailsRes.ok) {
        alert('Failed to get capture details');
        return;
      }
      const details = await detailsRes.json();
      if (!details.embedding || !Array.isArray(details.embedding)) {
        alert('No embedding found for this capture');
        return;
      }

      // --- Global Check for duplicate ---
      const duplicateCheck = await checkForDuplicate(details.embedding);
      if (duplicateCheck.duplicate) {
        const sim = duplicateCheck.existing?.similarity ?? 0;
        // v0.4.9.1.9: if similarity >= 0.99 it's effectively the same image.
        // No point offering "Add Anyway" — just inform the user it's already there.
        const isExact = sim >= 0.99;
        setDuplicateModal({
          existing: duplicateCheck.existing!,
          embedding: details.embedding,
          source: 'candidate',
          payload: capture,
          isExactMatch: isExact,
        });
        return;
      }

      // --- No duplicate found, proceed directly ---
      await performAddCandidate(capture, details.embedding);
    } catch (err: any) {
      alert(`Add error: ${err.message || err}`);
    }
  };

  const handleUploadFromDisk = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      alert('Please select an image file.');
      e.target.value = '';
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      alert('Image must be smaller than 10MB.');
      e.target.value = '';
      return;
    }

    setUploading(true);
    try {
      const reader = new FileReader();
      const dataUrl = await new Promise<string>((resolve, reject) => {
        reader.onload = () => resolve(reader.result as string);
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });

      // 1. Upload the image to VPS
      const uploadRes = await api.uploadPhoto(dataUrl);
      const photoUrl = uploadRes.url;  // now this is a VPS URL


      const embedRes = await api.post('/api/pois/compute-embedding', { photoUrl: photoUrl });
      if (!embedRes.embedding || !Array.isArray(embedRes.embedding)) {
        // v0.4.9.1.7 bug fix: log warning instead of alerting.
        // The alert broke the upload flow with a confusing message when
        // the photo had no face. The image was rejected entirely.
        // Now we log + skip the upload but don't show an alert. The user
        // can re-upload a different image.
        console.warn('[iSentinel360] No face detected in uploaded image — skipping (was: alert + abort)');
        setUploading(false);
        e.target.value = '';
        return;
      }

      // --- Global Check for duplicate ---
      const duplicateCheck = await checkForDuplicate(embedRes.embedding);
      if (duplicateCheck.duplicate) {
        const sim = duplicateCheck.existing?.similarity ?? 0;
        // v0.4.9.1.9: similarity >= 0.99 means it's the same image — don't offer Add Anyway
        const isExact = sim >= 0.99;
        setDuplicateModal({
          existing: duplicateCheck.existing!,
          embedding: embedRes.embedding,
          source: 'upload',
          payload: { dataUrl, file, preview: dataUrl },
          isExactMatch: isExact,
        });
        setUploading(false);
        e.target.value = '';
        return;
      }

      // --- No duplicate found, proceed directly ---
      await performUploadFromDisk(dataUrl, embedRes.embedding);
    } catch (err: any) {
      console.error('Upload error:', err);
      alert('Failed to upload image: ' + (err.message || 'unknown error'));
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const isAtCap = gallery.length >= MAX_GALLERY_IMAGES;
  const hasEdge = !!(edgeIp && edgePort);

  return (
    <>
      <div className="border rounded p-3 space-y-2 bg-muted/30">
        <div className="flex items-center justify-between">
          <h4 className="text-sm font-semibold">
            Gallery <span className="text-xs text-muted-foreground">({gallery.length}/{MAX_GALLERY_IMAGES})</span>
          </h4>
          {error && <p className="text-xs text-destructive">{error}</p>}
        </div>

        {!poiId ? (
          <p className="text-sm text-muted-foreground">{t('common.gallery.notAvailableYet')}</p>
        ) : loading ? (
          <p className="text-xs text-muted-foreground">{t('common.gallery.loading')}</p>
        ) : gallery.length === 0 ? (
          <p className="text-xs text-muted-foreground">{t('common.gallery.emptyGallery')}</p>
        ) : (
          <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2">
            {gallery.map(img => (
              <div key={img.id} className="relative group">
                {img.photoUrl && isValidImageUrl(img.photoUrl) ? (
                  <img src={img.photoUrl} alt="" className="w-full aspect-square object-cover rounded" />
                ) : (
                  <div className="w-full aspect-square bg-muted rounded flex items-center justify-center text-xs">N/A</div>
                )}
                {canRemove && gallery.length > 1 && (
                  <button
                    type="button"
                    onClick={() => handleRemove(img)}
                    className="absolute top-1 right-1 bg-black/60 text-white rounded-full p-0.5 opacity-0 group-hover:opacity-100 transition-opacity"
                    title="Remove image"
                  >
                    <X className="w-3 h-3" />
                  </button>
                )}
                <p className="text-[10px] text-muted-foreground truncate mt-0.5" title={img.justification}>
                  {img.justification}
                </p>
              </div>
            ))}
          </div>
        )}

        {poiId && !isAtCap && (
          <div className="border-t pt-2">
            <div className="flex flex-wrap gap-2">
              {/* Add from Recent button */}
              {!pickerOpen ? (
                <Button type="button" size="sm" variant="outline" onClick={() => setPickerOpen(true)} disabled={!hasEdge}>
                  <Plus className="w-3 h-3 mr-1" />
                  {t('common.gallery.addFromRecent')}
                  {!hasEdge && <span className="ml-2 text-[10px] text-muted-foreground">({t('common.edgeNotConfigured')})</span>}
                </Button>
              ) : (
                <Button type="button" size="sm" variant="outline" onClick={() => setPickerOpen(false)}>
                  {t('common.gallery.closeRecent')}
                </Button>
              )}

              {/* Upload from Disk button */}
              <label className="cursor-pointer">
                <span
                  className={`inline-flex items-center justify-center gap-2 px-3 py-1.5 text-sm font-medium rounded-md border ${
                    uploading
                      ? 'opacity-50 cursor-not-allowed border-muted-foreground/30 text-muted-foreground'
                      : 'border-input hover:bg-accent hover:text-accent-foreground'
                  }`}
                >
                  <Upload className="w-3 h-3" />
                  {uploading ? t('common.gallery.uploading') : t('common.gallery.uploadImage')}
                </span>
                <input
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={handleUploadFromDisk}
                  disabled={uploading}
                />
              </label>

              <span className="ml-auto text-xs text-muted-foreground">
                {gallery.length}/{MAX_GALLERY_IMAGES} used
              </span>
            </div>

            {/* Recent Picker content – identical to POIFormModal filter */}
            {pickerOpen && (
              <div className="space-y-2 mt-2">
                {/* Filter row – matches POIFormModal exactly */}
                <div className="flex flex-wrap gap-2 items-end">
                  <div>
                    {/* v0.4.9.1.8: use translation key (was hardcoded "Date") */}
                    <label className="text-xs">{t('guard.filterDate')}</label>
                    <input
                      type="date"
                      value={date}
                      onChange={e => { setDate(e.target.value); setRefreshTrigger(prev => prev + 1)}}
                      className="w-full p-1 border rounded text-xs bg-background"
                    />
                  </div>
                  <div>
                    <label className="text-xs">{t('guard.filterHour')}</label>
                    <select
                      value={hour}
                      onChange={e => { setHour(e.target.value === '' ? '' : parseInt(e.target.value)); setRefreshTrigger(prev => prev + 1); }}
                      className="w-full p-1 border rounded text-xs bg-background"
                    >
                      <option value="">{t('common.all')}</option>
                      {Array.from({ length: 24 }, (_, i) => (
                        <option key={i} value={i}>{String(i).padStart(2, '0')}:00</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="text-xs">{t('guard.filterMinBlock')}</label>
                    <select
                      value={minBlock}
                      onChange={e => { setMinBlock(e.target.value === '' ? '' : parseInt(e.target.value)); setRefreshTrigger(prev => prev + 1); }}
                      className="w-full p-1 border rounded text-xs bg-background"
                    >
                      <option value="">{t('common.all')}</option>
                      <option value="0">0‑9 min</option>
                      <option value="1">10‑19 min</option>
                      <option value="2">20‑29 min</option>
                      <option value="3">30‑39 min</option>
                      <option value="4">40‑49 min</option>
                      <option value="5">50‑59 min</option>
                    </select>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    type="button"
                    onClick={() => { setDate(''); setHour(''); setMinBlock(''); setRefreshTrigger(prev => prev + 1);}}
                  >
                    {t('common.reset')}
                  </Button>
                  <span className="ml-auto text-xs text-muted-foreground">
                    {candLoading ? t('common.loading') : candError ? <span className="text-destructive">{candError}</span> : t('duplicates.capturesCount', { count: candidates.length })}
                  </span>
                </div>

                {candidates.length === 0 && !candLoading ? (
                  <p className="text-xs text-muted-foreground border border-dashed rounded p-3 text-center">
                    {t('common.gallery.noCandidatesMatch')}
                  </p>
                ) : (
                  <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2 max-h-60 overflow-y-auto">
                    {candidates.map(c => (
                      <div key={c.id} className="border rounded p-1.5 space-y-1">
                        <EdgeCaptureImage edgeIp={edgeIp} edgePort={edgePort} captureId={c.id} alt={`Capture ${c.id}`} size="small" />
                        <p className="text-[10px] text-muted-foreground truncate" title={c.camera_name}>{c.camera_name}</p>
                        <p className="text-[10px] text-muted-foreground">{new Date(c.timestamp).toLocaleString()}</p>
                        <Button size="sm" type="button" onClick={() => handleAddCandidate(c)} className="w-full h-6 text-xs">
                          {t('common.add')}
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {isAtCap && (
          <p className="text-xs text-muted-foreground">{t('common.gallery.maxReached', { max: MAX_GALLERY_IMAGES })}</p>
        )}
      </div>

      {/* Custom Duplicate Modal - using a simple overlay instead of Dialog */}
      {duplicateModal && createPortal(
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <div className="bg-white dark:bg-gray-900 rounded-lg shadow-xl max-w-md w-full mx-4 p-6">
            <h2 className="text-xl font-bold mb-4">
              {duplicateModal.isExactMatch ? t('duplicates.duplicateExactTitle') : t('duplicates.duplicatePoiTitle')}
            </h2>
            <p className="text-sm text-muted-foreground mb-4">
              {duplicateModal.isExactMatch ? t('duplicates.duplicateExactBody') : t('duplicates.duplicatePoiBody')}
            </p>
            {typeof duplicateModal.existing.similarity === 'number' && (
              <p className="text-xs text-muted-foreground mb-2">
                {t('duplicates.similarityLabel')}: {(duplicateModal.existing.similarity * 100).toFixed(1)}%
              </p>
            )}
            <div className="flex gap-4 mb-4">
              <div className="border p-2 rounded flex-1">
                <p className="text-xs font-semibold">{t('duplicates.existing')}</p>
                <img 
                  src={resolveImageUrl(duplicateModal.existing.photoUrl)} 
                  className="w-full h-auto rounded border" 
                  onError={(e) => { e.currentTarget.src = '/images/default-avatar.png'; }}
                />
                <p className="text-sm mt-1 font-mono">{duplicateModal.existing.code}</p>
              </div>
              <div className="border p-2 rounded flex-1">
                <p className="text-xs font-semibold">{t('duplicates.newImage')}</p>
                {(() => {
                  // v0.4.9.1.15: 3-stage fallback chain (primary → ipHttps → fallback).
                  // See api.ts → buildEdgeImageSrc for the contract.
                  if (duplicateModal.source !== 'candidate') {
                    return (
                      <img
                        src={duplicateModal.payload.preview}
                        className="w-full h-auto rounded border"
                      />
                    );
                  }
                  const imgSrc = buildEdgeImageSrc(
                    { hostname: edgeHostname, edgeIp, edgePort },
                    `/captures/${duplicateModal.payload.id}`
                  );
                  return (
                    <img
                      src={imgSrc.primary}
                      className="w-full h-auto rounded border"
                      onError={(e) => {
                        const cur = e.currentTarget.src;
                        if (imgSrc.ipHttps && cur !== imgSrc.ipHttps) {
                          e.currentTarget.src = imgSrc.ipHttps;
                          return;
                        }
                        if (imgSrc.fallback && cur !== imgSrc.fallback) {
                          e.currentTarget.src = imgSrc.fallback;
                        }
                      }}
                    />
                  );
                })()}
              </div>
            </div>
            <div className="flex gap-2 justify-end">
              {/* v0.4.9.1.9: only show "Add Anyway" if it's NOT an exact match.
                  Exact matches (similarity ~1.0) can't be added — would just
                  duplicate an existing image. */}
              {!duplicateModal.isExactMatch && (
                <Button 
                  onClick={async () => {
                    const modalCopy = duplicateModal;
                    setDuplicateModal(null);
                    if (modalCopy.source === 'candidate') {
                      // bypassGalleryCheck: the user already acknowledged the global
                      // duplicate. Don't re-check internal gallery (which would otherwise
                      // reject since the same/similar face IS in the gallery).
                      await performAddCandidate(modalCopy.payload, modalCopy.embedding, { bypassGalleryCheck: true });
                    } else {
                      await performUploadFromDisk(modalCopy.payload.dataUrl, modalCopy.embedding, { bypassGalleryCheck: true });
                    }
                  }}
                >
                  {t('duplicates.addAnyway')}
                </Button>
              )}
              <Button variant="outline" onClick={() => setDuplicateModal(null)}>
                {t('common.cancel')}
              </Button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  );
}